"use client";

import React from "react";

import {
  getFlatOptions,
  isGroupedOptions,
  OptionsSorting,
  sortOptions,
} from "../lib/optionUtils";
import { cn } from "../lib/utils";
import {
  OptionGroup,
  Options,
  SelectedOptions,
  SetSelectedOptions,
  SingleOption,
} from "../types.ts/types";
import { Badge } from "./badge";
import { Button, buttonVariants } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandGroupClassName,
  CommandInput,
  CommandItem,
  CommandItemClassName,
  CommandList,
} from "./command";
import { Icon } from "./icon";
import { inputSectionClasses } from "./input";
import {
  Popover,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
} from "./popover";
import { Separator } from "./separator";

type SingleComboboxOption = React.ComponentPropsWithoutRef<typeof CommandItem> &
  SingleOption & {
    element?: React.ReactNode;
    onSelect?: (
      option: Pick<SingleComboboxOption, "value" | "label" | "disabled">,
    ) => unknown;
  };
type ComboboxOptionGroup = OptionGroup<SingleComboboxOption>;
type ComboboxOptions = Options<SingleComboboxOption>;

type useComboboxParams = {
  allowDeselect?: boolean;
  hasSearch?: boolean;

  /**
   * `src/lib/types.ts > SelectedOptions`
   * @type `string[]`
   */
  initialSelected?: SelectedOptions;

  isMultiselect?: boolean;
  options: ComboboxOptions;

  /**
   * `src/lib/types.ts > SelectedOptions`
   * @type `string[]`
   */
  selected?: SelectedOptions;

  /**
   * `src/lib/types.ts > SetSelectedOptions`
   * @type `(v: string[]) => void`
   */
  setSelected?: SetSelectedOptions;

  showCheck?: boolean;
  shrinkingList?: boolean;
  sorting?: OptionsSorting;
};
type useComboboxType = (props: useComboboxParams) => Pick<
  useComboboxParams,
  | "allowDeselect"
  | "hasSearch"
  | "isMultiselect"
  | "options"
  | "selected"
  | "setSelected"
  | "showCheck"
  | "shrinkingList"
> & {
  clearAll: () => void;
  flatOptions: ReturnType<typeof getFlatOptions<SingleComboboxOption>>;
  handleSelect: (v: SingleComboboxOption["value"]) => void;

  selected: Required<useComboboxParams>["selected"];
  setSelected: Required<useComboboxParams>["setSelected"];
};

/**
 * @param {Object} options - The options object
 * @param {boolean} [props.allowDeselect=true] - Whether to allow deselecting options
 * @param {boolean} [props.hasSearch=true] - This hides the search input and fixes its value, but
 * keeps it focused to allow keyboard navigation.
 * @param {boolean} [props.isMultiselect=true] - Whether to allow multiple selections
 * @param {ComboboxOptions} props.options - The options to display in the combobox
 * @param {boolean} [props.shrinkingList=false] - Whether to enable shrinking list functionality
 * @param {SingleComboboxOption[]} [props.initialSelected] - Initial selected options
 */
const useCombobox: useComboboxType = ({
  allowDeselect = true,
  hasSearch = true,
  initialSelected,
  isMultiselect = true,
  options,
  selected: externalSelected,
  setSelected: externalSetSelected,
  showCheck = true,
  shrinkingList = false,
  sorting = "asc",
}) => {
  options = sortOptions(options, sorting);
  const sortedInitialSelected = sortOptions(initialSelected ?? [], sorting);

  const [internalSelected, setInternalSelected] =
    React.useState<SelectedOptions>(
      getFlatOptions(sortedInitialSelected).map((v) => v.value),
    );

  const [selected, setSelected] = [
    externalSelected ?? internalSelected,
    externalSetSelected ?? setInternalSelected,
  ];

  const clearAll = () => {
    if (allowDeselect) setSelected([]);
  };

  const flatOptions = React.useMemo(() => {
    return getFlatOptions(options);
  }, [options]);

  const handleSelect = (option: SingleComboboxOption["value"]) => {
    let newSelected: SelectedOptions = [...selected];
    const selectedHasValue = selected.includes(option);

    if (selectedHasValue && allowDeselect) {
      // Value found and Deselect allowed:
      // Remove option whether multiselect is true or false
      newSelected = newSelected.filter((v) => v !== option);
    } else if (!selectedHasValue && !isMultiselect) {
      // Value not found and Multiselect disabled:
      // Only add one option
      newSelected = [option];
    } else if (!selectedHasValue && isMultiselect) {
      // Value not found and Multiselect enabled:
      // Add new option
      newSelected.push(option);
    }

    setSelected(
      // Only for neater output when logging
      sortOptions(newSelected, "asc") as SelectedOptions,
    );
  };

  return {
    allowDeselect,
    hasSearch,
    isMultiselect,
    options,
    showCheck,
    shrinkingList,

    selected,
    setSelected,

    clearAll,
    flatOptions,
    handleSelect,
  };
};

interface ComboboxItemProps
  extends
    Pick<
      ReturnType<useComboboxType>,
      "handleSelect" | "selected" | "showCheck"
    >,
    Pick<ComboboxProps, "classes"> {
  option: SingleComboboxOption;
}
const ComboboxItem = ({
  classes,
  handleSelect,
  option,
  selected,
  showCheck,
}: ComboboxItemProps) => (
  <CommandItem
    className={cn("flex items-center justify-between", classes?.dropdownItem)}
    disabled={option.disabled}
    key={option.value}
    keywords={[option.label ?? ""]}
    onSelect={() => {
      handleSelect(option.value);

      // Avoid a potential infinite recursion by excluding onSelect
      const { onSelect, ...rest } = option;
      onSelect?.(rest);
    }}
    value={option.value}
  >
    {option.element ?? option.label ?? option.value}

    {showCheck && (
      <Icon
        className={cn(
          selected.some((v) => v === option.value)
            ? "opacity-100"
            : "opacity-0",
        )}
        icon="check"
        size="!xs"
        weight="bold"
      />
    )}
  </CommandItem>
);

interface ComboboxItemsProps extends Pick<ComboboxProps, "classes"> {
  searchValue?: string;
  setSearchValue?: (v: string) => void;
  store: ReturnType<typeof useCombobox>;
}
const ComboboxItems = ({
  classes,
  searchValue,
  setSearchValue,
  store: {
    allowDeselect,
    clearAll,
    flatOptions,
    handleSelect,
    hasSearch,
    options,
    selected,
    shrinkingList,
    showCheck,
  },
}: ComboboxItemsProps) => {
  return (
    <>
      <div className={cn(!hasSearch && "h-0 overflow-hidden")}>
        <CommandInput
          // Keep CommandInput focused
          onBlur={(e) => {
            e.target.focus();
          }}
          onValueChange={hasSearch ? setSearchValue : undefined}
          placeholder="Search"
          value={hasSearch ? searchValue : ""}
        />
      </div>

      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {isGroupedOptions(options) ? (
          options.map((group) => (
            // If options have groups
            <CommandGroup heading={group.group} key={group.group}>
              {group.items.map((option) =>
                // When shrinkingList is enabled, only show options that are not selected
                shrinkingList &&
                selected.some((v) => v === option.value) ? null : (
                  <ComboboxItem
                    classes={classes}
                    handleSelect={handleSelect}
                    key={option.value}
                    option={option}
                    selected={selected}
                    showCheck={showCheck}
                  />
                ),
              )}
            </CommandGroup>
          ))
        ) : (
          // If options don't have groups
          <CommandGroup>
            {flatOptions.map((option) =>
              // When shrinkingList is enabled, only show options that are not selected
              shrinkingList &&
              selected.some((v) => v === option.value) ? null : (
                <ComboboxItem
                  classes={classes}
                  handleSelect={handleSelect}
                  key={option.value}
                  option={option}
                  selected={selected}
                  showCheck={showCheck}
                />
              ),
            )}
          </CommandGroup>
        )}
      </CommandList>

      {allowDeselect && selected.length > 0 && (
        <>
          <Separator />

          {/* Don't use CommandGroup and CommandItem here as it's
           * being filtered out when searching.
           */}
          <div className={CommandGroupClassName}>
            <button
              className={cn(
                buttonVariants({ variant: "ghost" }),
                CommandItemClassName,
                "w-full cursor-pointer text-destructive",
              )}
              onClick={(e) => {
                e.preventDefault();
                clearAll();
              }}
            >
              Clear ({selected.length})
            </button>
          </div>
        </>
      )}
    </>
  );
};

interface ComboboxTriggerProps extends React.ComponentPropsWithoutRef<"button"> {
  hasCount?: boolean;
  multipleSelectedPlaceholder?: React.ReactNode;
  placeholder?: React.ReactNode;
  showAll?: boolean;
  store: ReturnType<typeof useCombobox>;
}

const ComboboxTrigger = React.forwardRef<
  HTMLButtonElement,
  ComboboxTriggerProps
>(
  (
    {
      className,
      hasCount = true,
      multipleSelectedPlaceholder = "Selected",
      placeholder = "Select",
      showAll,
      store,
      ...props
    },
    ref,
  ) => {
    const { selected } = store;

    let triggerChildren: React.ReactNode;
    if (selected.length === 0) {
      triggerChildren = placeholder;
    } else if (selected.length > 1 && !showAll) {
      triggerChildren = multipleSelectedPlaceholder;
    } else {
      triggerChildren = store.flatOptions
        .filter((v) => selected.includes(v.value))
        .map((v) => v.label ?? v.value)
        .join(", ");
    }

    /**
     * Ensure that the Combobox trigger matches the
     * design of the Select trigger.
     */
    return (
      <Button
        className={cn(
          "relative w-full bg-card pl-3 pr-8 text-left font-normal data-[placeholder]:text-muted-foreground",
          selected.length > 1 && "pr-[4rem]",
          showAll && "h-fit text-wrap",
          className,
        )}
        data-placeholder={selected.length === 0 ? true : null}
        overrideClass={false}
        ref={ref}
        variant="outline"
        {...props}
      >
        <span
          className={cn(
            "pointer-events-none min-w-full leading-[1.3]",
            // Set width to specific value so truncate works on children
            !showAll && "w-0 truncate break-words",
          )}
        >
          {props?.children || triggerChildren}
        </span>

        {hasCount && selected.length > 1 && (
          <Badge
            className={cn(
              inputSectionClasses({ faded: false }),
              "right-[1.875rem] rounded-full",
            )}
          >
            {selected.length}
          </Badge>
        )}

        {store.allowDeselect && selected.length > 0 ? (
          <div
            aria-label="Clear"
            className={inputSectionClasses({
              side: "right",
              pointerEvents: true,
              pointer: true,
            })}
            onClick={(e) => {
              e.preventDefault();
              if (store.allowDeselect) store.clearAll();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && store.allowDeselect) {
                e.preventDefault();
                store.clearAll();
              }
            }}
            role="button"
            // Make the div focusable and keyboard accessible
            tabIndex={0}
          >
            <Icon icon="x" size="!xs" weight="bold" />
          </div>
        ) : (
          <Icon
            className={inputSectionClasses({ side: "right" })}
            icon="chevronDown"
            size="!xs"
            weight="bold"
          />
        )}
      </Button>
    );
  },
);
ComboboxTrigger.displayName = "ComboboxTrigger";

interface ComboboxProps
  extends
    ComboboxItemsProps,
    // Omit 'store' from 'triggerProps' to prevent multiple stores
    // from being passed to the combobox.
    Omit<ComboboxTriggerProps, "className" | "store"> {
  classes?: {
    dropdownTrigger?: string;
    dropdownItem?: string;
  };
  popoverProps?: PopoverProps;
  trigger?: (props: ComboboxTriggerProps) => React.ReactNode;
}

/**
 * API reference: https://github.com/pacocoursey/cmdk
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/combobox
 *
 * @param options It expects a `options[i].value` to be unique to function
 * correctly. You can use `options[i].value` as both the `value` and `label`.
 *
 * If using `group`(s), every element in `options` **MUST** have a `group` property.
 */
const Combobox = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- lint-backlog: binding kept on purpose (prop API, rest-spread exclusion or declaration-merge signature); removing it changes the API
  children,
  classes = {},
  searchValue,
  setSearchValue,
  store,
  trigger,
  popoverProps,

  ...props
}: ComboboxProps) => (
  <Popover {...popoverProps}>
    <PopoverTrigger asChild>
      {trigger ? (
        trigger({ className: classes.dropdownTrigger, store, ...props })
      ) : (
        <ComboboxTrigger
          className={classes.dropdownTrigger}
          store={store}
          {...props}
        />
      )}
    </PopoverTrigger>

    <PopoverContent
      className="w-[--radix-popover-trigger-width] p-0"
      /**
       * https://github.com/radix-ui/primitives/issues/1159#issuecomment-1741282769
       *
       * There is a known issue with Radix UI that prevents the PopoverContent
       * from being scrolled when it is inside a Dialog (modal).
       *
       * Disabling the wheel event from propagating to the parent allows
       * scrolling to work as expected.
       */
      onWheel={(e) => e.stopPropagation()}
    >
      <Command>
        <ComboboxItems
          classes={classes}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          store={store}
        />
      </Command>
    </PopoverContent>
  </Popover>
);

export {
  Combobox,
  ComboboxItems,
  ComboboxTrigger,
  useCombobox,
  type ComboboxItemsProps,
  type ComboboxOptionGroup,
  type ComboboxOptions,
  type ComboboxProps,
  type ComboboxTriggerProps,
  type SelectedOptions,
  type SetSelectedOptions,
  type SingleComboboxOption,
  type useComboboxParams,
  type useComboboxType,
};
